import type {
  AboutHeroSection,
  AboutSection,
  AchievementsSection,
  CategoriesSection,
  ClientsSection,
  ClientStorySection,
  CoreValuesSection,
  FooterSection,
  HeroSection,
  IndustriesSection,
  MaterialsSection,
  PointsSection,
  RecentProjectSection,
} from "../api/homePageApi";

import HeroBannerDesktop from "../assets/img/HeroImage.png";
import HeroBannerMobile from "../assets/img/HeroBannerMobile.png";
import product1 from "../assets/img/produc1.png";
import product2 from "../assets/img/product2.png";
import product3 from "../assets/img/product4.jpg";
import product4 from "../assets/img/produc3.png";
import WorkerImage from "../assets/img/Worker.jpg";
import vectorIcon1 from "../assets/img/Vector1.png";
import vectorIcon2 from "../assets/img/Vector2.png";
import vectorIcon3 from "../assets/img/Vector3.png";
import vectorIcon4 from "../assets/img/Vector4.png";
import vectorIcon5 from "../assets/img/Vector5.png";
import FooterLogo from "../assets/img/logo white.png";
import VisionImage from "../assets/img/Visi.jpg";
import MissionImage from "../assets/img/Misi.png";
import handIcon from "../assets/img/6f7847fda55a8ec939a8034da96eae549c1d2cf2.png";
import rewardIcon from "../assets/img/c25d9c242b8847d6c31f76c12e7437bb2c8740b4.png";
import thumbIcon from "../assets/img/dd90a7ba3621e43f4e2b22fa3b29c88ed500b92f.png";
import lampIcon from "../assets/img/ef0c8584e40c80d0552fa1dd6a029bad78119cb2.png";

/**
 * Konten Home = data dari admin (CMS) + konten bawaan.
 *
 * ATURAN (berlaku untuk semua section):
 *  1. Section yang BELUM PERNAH disimpan admin (`updated_at` null), atau API
 *     gagal -> konten bawaan di bawah (sama dengan tampilan sebelum ada CMS).
 *  2. Section yang SUDAH disimpan -> isi CMS jadi acuan. Field wajib yang
 *     entah kenapa kosong tetap jatuh ke konten bawaan; field opsional yang
 *     dikosongkan admin berarti "tidak ditampilkan" (mis. tombol, ikon).
 *  3. Daftar item yang disimpan kosong (mis. Client Story dikosongkan) =
 *     section disembunyikan, bukan diganti konten bawaan.
 */

import type { Language } from "../context/LanguageContext";

type Saved = { updated_at: string | null } | null | undefined;

const isSaved = (section: Saved): boolean => Boolean(section?.updated_at);

/** Field wajib: kosong -> pakai bawaan. */
const text = (value: string | null | undefined, fallback: string): string =>
  value && value.trim() !== "" ? value : fallback;

/** Field opsional: kosong -> "" (tidak ditampilkan). */
const optional = (value: string | null | undefined): string =>
  value?.trim() ?? "";

/**
 * Pilih nilai mentah sesuai bahasa aktif untuk field translatable (yang
 * punya pasangan `<field>` / `<field>_en` dari CMS).
 *
 * EN kosong/belum diisi admin -> tetap jatuh ke nilai Indonesia (bukan
 * langsung ke konten bawaan section), supaya pengunjung yang pilih English
 * tetap lihat konten yang sudah diisi admin alih-alih placeholder generik,
 * selama admin belum sempat menerjemahkannya.
 */
const pick = (
  language: Language,
  idValue: string | null | undefined,
  enValue: string | null | undefined
): string | null | undefined =>
  language === "en" && enValue && enValue.trim() !== "" ? enValue : idValue;

// Kalimat yang sudah "selesai": berakhir dengan tanda baca penutup.
const SENTENCE_END = /[.!?:;…”")\]]$/;
// Awal butir daftar ("- ", "• ", "1. ", "2) ").
const LIST_MARKER = /^([-•*–]|\d+[.)])\s/;
// Baris lebih pendek dari ini dianggap sengaja pendek (judul, nama), bukan patahan.
const HARD_WRAP_MIN_LENGTH = 45;

/**
 * Baris baru di textarea admin (Enter) = paragraf baru.
 *
 * Pengecualian: teks yang ditempel dari dokumen/PDF sering sudah dipatahkan di
 * tengah kalimat ("...perusahaan yang" / "berdiri pada tahun 2020"). Baris
 * seperti itu digabung kembali dengan baris berikutnya, supaya satu paragraf
 * tidak berubah menjadi belasan paragraf. Sebuah baris dianggap patahan bila
 * cukup panjang, tidak diakhiri tanda baca penutup (. ! ? : ; dst), dan baris
 * berikutnya bukan butir daftar.
 */
const toParagraphs = (value: string | null | undefined): string[] => {
  const paragraphs: string[] = [];

  (value ?? "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const previous = paragraphs[paragraphs.length - 1];

      const continuesPrevious =
        previous !== undefined &&
        previous.length >= HARD_WRAP_MIN_LENGTH &&
        !SENTENCE_END.test(previous) &&
        !LIST_MARKER.test(line);

      if (continuesPrevious) {
        paragraphs[paragraphs.length - 1] = `${previous} ${line}`;
      } else {
        paragraphs.push(line);
      }
    });

  return paragraphs;
};

/* -------------------------------------------------------------------------- */
/*  Hero                                                                       */
/* -------------------------------------------------------------------------- */

export interface HeroContent {
  title: string;
  buttonLabel: string;
  buttonLink: string;
  desktopImage: string;
  mobileImage: string;
}

const DEFAULT_HERO: HeroContent = {
  title: "Solusi Seal Industri\nPresisi Tinggi &\nManufaktur Kustom.",
  buttonLabel: "Call Now",
  buttonLink: "/Contact",
  desktopImage: HeroBannerDesktop,
  mobileImage: HeroBannerMobile,
};

export const resolveHero = (
  section: HeroSection | null | undefined,
  language: Language
): HeroContent => {
  if (!section || !isSaved(section)) return DEFAULT_HERO;

  return {
    title: text(pick(language, section.title, section.title_en), DEFAULT_HERO.title),
    buttonLabel: text(
      pick(language, section.button_label, section.button_label_en),
      DEFAULT_HERO.buttonLabel
    ),
    // Tombol tanpa tujuan tidak berguna -> link kosong pakai bawaan.
    // Link BUKAN konten kalimat -> tidak diterjemahkan, sama di kedua bahasa.
    buttonLink: text(section.button_link, DEFAULT_HERO.buttonLink),
    // CMS hanya punya satu gambar hero: dipakai untuk desktop dan mobile.
    desktopImage: section.image || DEFAULT_HERO.desktopImage,
    mobileImage: section.image || DEFAULT_HERO.mobileImage,
  };
};

/* -------------------------------------------------------------------------- */
/*  Product Highlights (card kategori)                                         */
/* -------------------------------------------------------------------------- */

export interface CategoryCardContent {
  key: string | number;
  title: string;
  description: string;
  image: string;
  link: string;
  gradient: string;
}

// Warna card bagian dari desain (bukan konten) -> bergilir berdasarkan urutan.
// Semua card produk pakai gradient yang sama (bukan variasi per index lagi).
const CATEGORY_GRADIENTS = ["from-[#3DA4A8] to-[#364A55]"];

const DEFAULT_CATEGORIES: CategoryCardContent[] = [
  {
    key: "seal-product",
    title: "Seal Product",
    image: product1,
    description: "Menyediakan berbagai Seal Product",
    gradient: CATEGORY_GRADIENTS[0],
    link: "/products/seal-product",
  },
  {
    key: "mechanical-seal",
    title: "Mechanical Seal",
    image: product2,
    description: "Menyediakan berbagai Mechanical Seal",
    gradient: CATEGORY_GRADIENTS[1],
    link: "/products/mechanical-seal",
  },
  {
    key: "custom-product",
    title: "Custom Product",
    image: product4,
    description: "Melayani Custom Product Seals",
    gradient: CATEGORY_GRADIENTS[2],
    link: "/products/custom-product",
  },
  {
    key: "service",
    title: "Service",
    image: product3,
    description: "Melayani Service",
    gradient: CATEGORY_GRADIENTS[3],
    link: "/products/service-hydraulic",
  },
];

export const resolveCategories = (
  section: CategoriesSection | null | undefined,
  language: Language
): CategoryCardContent[] => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_CATEGORIES;
  }

  return section.items.flatMap((item, index) =>
    item.image
      ? [
        {
          key: item.id,
          title: optional(pick(language, item.title, item.title_en)),
          description: optional(pick(language, item.description, item.description_en)),
          image: item.image,
          // Link kosong = card tidak bisa diklik.
          link: optional(item.link),
          gradient: CATEGORY_GRADIENTS[index % CATEGORY_GRADIENTS.length],
        },
      ]
      : []
  );
};

/* -------------------------------------------------------------------------- */
/*  About                                                                      */
/* -------------------------------------------------------------------------- */

export interface AboutContent {
  label: string;
  paragraphs: string[];
  buttonLabel: string;
  buttonLink: string;
  image: string;
}

const DEFAULT_ABOUT: AboutContent = {
  label: "Tentang Kami",
  paragraphs: [
    "PT. ARUNIKA MEDINA PRATAMA adalah sebuah perusahaan yang berdiri pada tahun 2020.",
    "Perusahaan ini dipercaya sebagai mitra pemasaran seal untuk berbagai merek ternama seperti Merkel, NOK, SIMRIT, NA, dan Valqua.",
    "Didukung tenaga profesional berpengalaman untuk menyediakan produk berkualitas dan layanan terbaik.",
    "Menyediakan layanan proses produksi yang cepat, presisi, dan solusi untuk kebutuhan industri.",
  ],
  buttonLabel: "About Us",
  buttonLink: "/About",
  image: WorkerImage,
};

export const resolveAbout = (
  section: AboutSection | null | undefined,
  language: Language
): AboutContent => {
  if (!section || !isSaved(section)) return DEFAULT_ABOUT;

  const paragraphs = toParagraphs(pick(language, section.description, section.description_en));

  return {
    label: text(pick(language, section.section_title, section.section_title_en), DEFAULT_ABOUT.label),
    paragraphs: paragraphs.length > 0 ? paragraphs : DEFAULT_ABOUT.paragraphs,
    // Label tombol kosong = tombol disembunyikan.
    buttonLabel: optional(pick(language, section.button_label, section.button_label_en)),
    // Link BUKAN konten kalimat -> tidak diterjemahkan.
    buttonLink: text(section.button_link, DEFAULT_ABOUT.buttonLink),
    image: section.image || DEFAULT_ABOUT.image,
  };
};

/* -------------------------------------------------------------------------- */
/*  Recent Project (judul & tombol; daftar proyek dari /Project/List)          */
/* -------------------------------------------------------------------------- */

export interface RecentProjectContent {
  title: string;
  buttonLabel: string;
  buttonLink: string;
}

const DEFAULT_RECENT_PROJECT: RecentProjectContent = {
  title: "Recent Project",
  buttonLabel: "View All",
  buttonLink: "/Project",
};

export const resolveRecentProject = (
  section: RecentProjectSection | null | undefined,
  language: Language
): RecentProjectContent => {
  if (!section || !isSaved(section)) return DEFAULT_RECENT_PROJECT;

  return {
    title: text(
      pick(language, section.section_title, section.section_title_en),
      DEFAULT_RECENT_PROJECT.title
    ),
    buttonLabel: optional(pick(language, section.button_label, section.button_label_en)),
    // Link BUKAN konten kalimat -> tidak diterjemahkan.
    buttonLink: text(section.button_link, DEFAULT_RECENT_PROJECT.buttonLink),
  };
};

/* -------------------------------------------------------------------------- */
/*  Quality Materials                                                          */
/* -------------------------------------------------------------------------- */

export interface MaterialContent {
  key: string | number;
  name: string;
  description: string;
}

export interface MaterialsContent {
  title: string;
  items: MaterialContent[];
}

const DEFAULT_MATERIALS: MaterialsContent = {
  title: "Quality Materials",
  items: [
    { key: "nbr", name: "NBR", description: "Nitrile Rubber" },
    { key: "epdm", name: "EPDM", description: "Ethylene Propylene" },
    { key: "ptfe", name: "PTFE", description: "Polytetrafluoroethylene" },
    { key: "pu", name: "PU", description: "Polyurethane" },
    { key: "sil", name: "SIL", description: "Silicone" },
  ],
};

export const resolveMaterials = (
  section: MaterialsSection | null | undefined,
  language: Language
): MaterialsContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_MATERIALS;
  }

  return {
    title: text(
      pick(language, section.section_title, section.section_title_en),
      DEFAULT_MATERIALS.title
    ),
    items: section.items.map((item) => ({
      key: item.id,
      // Kode material (NBR, EPDM, dst) universal -> tidak diterjemahkan.
      name: optional(item.name),
      description: optional(pick(language, item.description, item.description_en)),
    })),
  };
};

/* -------------------------------------------------------------------------- */
/*  Serving Various Industries                                                 */
/* -------------------------------------------------------------------------- */

export interface IndustryContent {
  key: string | number;
  name: string;
  icon: string;
}

export interface IndustriesContent {
  title: string;
  items: IndustryContent[];
}

const DEFAULT_INDUSTRIES: IndustriesContent = {
  // Sebelumnya tertulis "Quality Materials" (salinan section Materials);
  // judul yang benar sesuai desain adalah "Serving Various Industries".
  title: "Serving Various Industries",
  items: [
    { key: "1", name: "Ethylene Propylene", icon: vectorIcon1 },
    { key: "2", name: "Polytetrafluoroethylene", icon: vectorIcon2 },
    { key: "3", name: "Polyurethane", icon: vectorIcon3 },
    { key: "4", name: "Silicone", icon: vectorIcon4 },
    { key: "5", name: "Nitrile Rubber", icon: vectorIcon5 },
  ],
};

export const resolveIndustries = (
  section: IndustriesSection | null | undefined,
  language: Language
): IndustriesContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_INDUSTRIES;
  }

  return {
    title: text(
      pick(language, section.section_title, section.section_title_en),
      DEFAULT_INDUSTRIES.title
    ),
    items: section.items.flatMap((item) =>
      item.icon
        ? [
          {
            key: item.id,
            name: optional(pick(language, item.name, item.name_en)),
            icon: item.icon,
          },
        ]
        : []
    ),
  };
};

/* -------------------------------------------------------------------------- */
/*  Our Client (logo)                                                          */
/* -------------------------------------------------------------------------- */

export interface ClientLogoContent {
  key: string | number;
  name: string;
  logo: string;
}

export interface ClientsContent {
  title: string;
  items: ClientLogoContent[];
}

export const resolveClients = (
  section: ClientsSection | null | undefined,
  language: Language
): ClientsContent => {
  // Tidak ada logo bawaan: sebelum admin mengisi, section ini tidak tampil.
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return { title: "Our Client", items: [] };
  }

  return {
    title: text(pick(language, section.section_title, section.section_title_en), "Our Client"),
    items: section.items.flatMap((item) =>
      item.logo
        // Nama perusahaan (proper noun) -> tidak diterjemahkan.
        ? [{ key: item.id, name: optional(item.name), logo: item.logo }]
        : []
    ),
  };
};

/* -------------------------------------------------------------------------- */
/*  Client Story                                                               */
/* -------------------------------------------------------------------------- */

export interface ClientStoryContent {
  key: string | number;
  name: string;
  message: string;
  /** null = pakai avatar inisial nama. */
  photo: string | null;
}

export interface ClientStoriesContent {
  title: string;
  items: ClientStoryContent[];
}

const DEFAULT_CLIENT_STORIES: ClientStoriesContent = {
  title: "Client Story",
  items: [
    {
      key: "1",
      name: "Ahmad Khoir",
      message:
        "Seal dan ring dari PT Arunika Medina Pratama kualitasnya sangat baik. Presisi dan tahan lama",
      photo: null,
    },
    {
      key: "2",
      name: "Nawila Larasati",
      message:
        "Pelayanan cepat dan produknya sesuai spesifikasi. Seal yang kami beli dari PT Arunika Medina Pratama terbukti awet dan tidak mudah bocor.",
      photo: null,
    },
    {
      key: "3",
      name: "Erik Sudirman",
      message:
        "Sudah beberapa kali order ring di sini, hasilnya selalu memuaskan. Kualitas konsisten dan pengiriman juga tepat waktu.",
      photo: null,
    },
    {
      key: "4",
      name: "Budi Santoso",
      message:
        "Produk yang dikirim sangat presisi dan kualitas materialnya sesuai kebutuhan industri kami.",
      photo: null,
    },
    {
      key: "5",
      name: "Rina Putri",
      message:
        "Customer service sangat responsif dan pengiriman produk cepat serta aman.",
      photo: null,
    },
  ],
};

export const resolveClientStories = (
  section: ClientStorySection | null | undefined,
  language: Language
): ClientStoriesContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_CLIENT_STORIES;
  }

  return {
    title: text(
      pick(language, section.section_title, section.section_title_en),
      DEFAULT_CLIENT_STORIES.title
    ),
    items: section.items.map((item) => ({
      key: item.id,
      // Nama orang (proper noun) -> tidak diterjemahkan.
      name: optional(item.name),
      message: optional(pick(language, item.quote, item.quote_en)),
      photo: item.photo || null,
    })),
  };
};

/* -------------------------------------------------------------------------- */
/*  Pencapaian Industri (tampil di Footer)                                     */
/* -------------------------------------------------------------------------- */

export interface AchievementContent {
  key: string | number;
  value: string;
  label: string;
}

export interface AchievementsContent {
  /** "" = tanpa judul (tampilan bawaan footer memang tidak punya judul). */
  title: string;
  items: AchievementContent[];
}

const DEFAULT_ACHIEVEMENTS: AchievementsContent = {
  title: "",
  items: [
    { key: "1", value: "3.000+", label: "Jumlah Produk" },
    { key: "2", value: "12.000+", label: "Produk Terjual" },
    { key: "3", value: "1643", label: "Pelanggan Aktif" },
  ],
};

export const resolveAchievements = (
  section: AchievementsSection | null | undefined,
  language: Language
): AchievementsContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_ACHIEVEMENTS;
  }

  return {
    title: optional(pick(language, section.section_title, section.section_title_en)),
    items: section.items.map((item) => ({
      key: item.id,
      // Angka -> tidak diterjemahkan.
      value: optional(item.value),
      label: optional(pick(language, item.label, item.label_en)),
    })),
  };
};

/* -------------------------------------------------------------------------- */
/*  Footer & kontak                                                            */
/* -------------------------------------------------------------------------- */

export interface FooterContent {
  headOffice: string;
  workshop: string;
  /** "" = ikon tidak ditampilkan. */
  phone: string;
  whatsapp: string;
  email: string;
  instagram: string;
  logo: string;
}

const DEFAULT_ADDRESS = "Komplek Pondok Ungu\nPermai Blok C15 no. 4\nBekasi Utara";

const DEFAULT_FOOTER: FooterContent = {
  headOffice: DEFAULT_ADDRESS,
  workshop: DEFAULT_ADDRESS,
  phone: "+62218838000",
  whatsapp: "6281234567890",
  email: "arunikamedinapratama@gmail.com",
  instagram: "https://instagram.com/arunikamedinapratama",
  logo: FooterLogo,
};

export const resolveFooter = (section?: FooterSection | null): FooterContent => {
  if (!section || !isSaved(section)) return DEFAULT_FOOTER;

  return {
    headOffice: text(section.head_office, DEFAULT_FOOTER.headOffice),
    workshop: text(section.workshop, DEFAULT_FOOTER.workshop),
    phone: optional(section.phone),
    whatsapp: optional(section.whatsapp),
    email: optional(section.email),
    instagram: optional(section.instagram),
    logo: section.logo || DEFAULT_FOOTER.logo,
  };
};

/** Nomor WhatsApp untuk tombol melayang (bawaan lamanya berbeda dari footer). */
const DEFAULT_FLOATING_WHATSAPP = "628123456789";

export const resolveFloatingWhatsapp = (
  section?: FooterSection | null
): string =>
  section && isSaved(section)
    ? optional(section.whatsapp)
    : DEFAULT_FLOATING_WHATSAPP;

/* -------------------------------------------------------------------------- */
/*  Halaman Contact (data yang sama dengan Footer & Contact di admin)          */
/* -------------------------------------------------------------------------- */

export interface ContactPageContent {
  /** "" = baris tidak ditampilkan. */
  email: string;
  phone: string;
  whatsapp: string;
  /** "@akun" atau URL; yang ditampilkan hanya nama akunnya. */
  instagram: string;
  headOffice: string;
  workshop: string;
}

// Nilai bawaan halaman Contact berbeda dari Footer (memang begitu sebelum ada
// CMS), jadi dipertahankan terpisah agar tampilan sebelum diisi admin sama.
const DEFAULT_CONTACT_PAGE: ContactPageContent = {
  email: "arunikamedinapratama@gmail.com",
  phone: "021 753 4342",
  whatsapp: "0812 1234 1234",
  instagram: "@arunikamedinapratama",
  headOffice: "Komplek Pondok Ungu Permai Blok C15 No.4, Bekasi Utara",
  workshop: "Komplek Pondok Ungu Permai Blok C15 No.4, Bekasi Utara",
};

export const resolveContactPage = (
  section?: FooterSection | null
): ContactPageContent => {
  if (!section || !isSaved(section)) return DEFAULT_CONTACT_PAGE;

  return {
    email: optional(section.email),
    phone: optional(section.phone),
    whatsapp: optional(section.whatsapp),
    instagram: optional(section.instagram),
    headOffice: text(section.head_office, DEFAULT_CONTACT_PAGE.headOffice),
    workshop: text(section.workshop, DEFAULT_CONTACT_PAGE.workshop),
  };
};

/* -------------------------------------------------------------------------- */
/*  Halaman About Us                                                           */
/* -------------------------------------------------------------------------- */

export interface AboutHeroContent {
  title: string;
  paragraphs: string[];
  image: string;
}

const DEFAULT_ABOUT_HERO: AboutHeroContent = {
  title: "Tentang Kami",
  paragraphs: [
    "PT. ARUNIKA MEDINA PRATAMA adalah sebuah perusahaan yang berdiri pada tahun 2020. Perusahaan ini telah dipercaya untuk menjadi mitra dalam pemasaran seal dan perusahaan seal terkenal diantaranya: merkel, NOK, SIMRIT, N-A, Valqua, dan lain sebagainya.",
    "PT. ARUNIKA MEDINA PRATAMA didukung oleh tenaga profesional berpengalaman. Untuk memenuhi tuntutan pasar dan perkembangan yang begitu pesat, kami menyediakan produk dengan kualitas terbaik dan layanan profesional.",
  ],
  image: WorkerImage,
};

export const resolveAboutHero = (
  section: AboutHeroSection | null | undefined,
  language: Language
): AboutHeroContent => {
  if (!section || !isSaved(section)) return DEFAULT_ABOUT_HERO;

  const paragraphs = toParagraphs(pick(language, section.description, section.description_en));

  return {
    title: text(pick(language, section.title, section.title_en), DEFAULT_ABOUT_HERO.title),
    paragraphs:
      paragraphs.length > 0 ? paragraphs : DEFAULT_ABOUT_HERO.paragraphs,
    image: section.image || DEFAULT_ABOUT_HERO.image,
  };
};

/** Visi dan Misi punya bentuk yang sama: judul, foto, dan daftar poin. */
export interface PointsContent {
  title: string;
  image: string;
  points: string[];
}

const DEFAULT_VISION: PointsContent = {
  title: "VISI",
  image: VisionImage,
  points: [
    "Menjadi perusahaan yang profesional dengan pengelolaan sumber daya manusia yang optimal dalam memberikan kepuasan terhadap klien.",
    'Menjadikan perusahaan yang "PROFESIONAL, MODERN, TERPERCAYA DAN GOOD AFTER SALES".',
    "Berkomitmen membuka lapangan pekerjaan dan menciptakan tenaga profesional yang ahli.",
  ],
};

const DEFAULT_MISSION: PointsContent = {
  title: "MISI",
  image: MissionImage,
  points: [
    "Membangun perusahaan dengan mengedepankan kepuasan konsumen.",
    "Melatih dan mengembangkan SDM untuk menjadi profesional.",
    "Menyediakan barang sesuai spesifikasi, berkualitas, dan terjamin keasliannya.",
    "Memberikan penawaran dengan harga kompetitif.",
    "Menjaga service level pengiriman yang profesional dan kepuasan klien dalam after sales.",
  ],
};

const resolvePoints = (
  section: PointsSection | null | undefined,
  language: Language,
  fallback: PointsContent
): PointsContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return fallback;
  }

  return {
    title: text(pick(language, section.section_title, section.section_title_en), fallback.title),
    image: section.image || fallback.image,
    points: section.items
      .map((item) => optional(pick(language, item.text, item.text_en)))
      .filter(Boolean),
  };
};

export const resolveVision = (
  section: PointsSection | null | undefined,
  language: Language
): PointsContent => resolvePoints(section, language, DEFAULT_VISION);

export const resolveMission = (
  section: PointsSection | null | undefined,
  language: Language
): PointsContent => resolvePoints(section, language, DEFAULT_MISSION);

export interface CoreValueContent {
  key: string | number;
  icon: string;
  title: string;
  description: string;
}

export interface CoreValuesContent {
  title: string;
  items: CoreValueContent[];
}

const DEFAULT_CORE_VALUES: CoreValuesContent = {
  title: "Nilai Utama Kami",
  items: [
    {
      key: "professional",
      icon: handIcon,
      title: "Professional",
      description: "Pengelolaan SDM Optimal untuk kepuasan klient tertinggi",
    },
    {
      key: "modern",
      icon: lampIcon,
      title: "Modern",
      description:
        "Menggunakan teknologi CNC presisi dan sistem manufaktur terkini.",
    },
    {
      key: "terpercaya",
      icon: rewardIcon,
      title: "Terpercaya",
      description: "Menjamin keaslian barang sesuai spesifik teknis",
    },
    {
      key: "good-after-sales",
      icon: thumbIcon,
      title: "Good After Sales",
      description:
        "Layanan purna jual yang memastikan operasional pelanggan tetap lancar",
    },
  ],
};

export const resolveCoreValues = (
  section: CoreValuesSection | null | undefined,
  language: Language
): CoreValuesContent => {
  if (!section || !isSaved(section) || !Array.isArray(section.items)) {
    return DEFAULT_CORE_VALUES;
  }

  return {
    title: text(
      pick(language, section.section_title, section.section_title_en),
      DEFAULT_CORE_VALUES.title
    ),
    items: section.items.flatMap((item) =>
      item.icon
        ? [
          {
            key: item.id,
            icon: item.icon,
            title: optional(pick(language, item.title, item.title_en)),
            description: optional(pick(language, item.description, item.description_en)),
          },
        ]
        : []
    ),
  };
};